/**
 * LivePulse — demo-only "real-time" affordances.
 *  • <LivePulse sources={[...]} />  a pulsing green "Live · synced Xs ago" chip
 *    that cycles through the connected integration names so the demo reads as
 *    a live, connected system.
 *  • useLiveValue(base, opts)  returns a number that gently drifts every few
 *    seconds (small ± jitter around the base) so headline metrics feel live.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Radio } from 'lucide-react';

const DEFAULT_SOURCES = ['Square', 'Toast', 'Lightspeed', 'Uber Eats', 'DoorDash', 'Tanda', 'Doshii'];

export function LivePulse({ sources = DEFAULT_SOURCES, className = '', label = 'Live' }) {
  const [secs, setSecs] = useState(3);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const tick = setInterval(() => {
      setSecs((s) => {
        const next = s + 1;
        if (next > 14) {
          // "new sync" — reset the counter and rotate the source shown
          setIdx((i) => (i + 1) % sources.length);
          return 1;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [sources.length]);

  return (
    <span
      data-testid="live-pulse"
      className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-emerald-300/60 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 ${className}`}
    >
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      <Radio className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate">{label} · synced from <b className="font-semibold">{sources[idx]}</b> · {secs}s ago</span>
    </span>
  );
}

export function useLiveValue(base, { pct = 0.015, intervalMs = 2600, drift = 0 } = {}) {
  const [val, setVal] = useState(base);
  const baseRef = useRef(base);
  const tRef = useRef(0);

  useEffect(() => { baseRef.current = base; setVal(base); }, [base]);

  useEffect(() => {
    if (!base) return;
    const id = setInterval(() => {
      tRef.current += 1;
      const jitter = (Math.random() - 0.5) * 2 * pct * baseRef.current;
      const trend = drift * baseRef.current * tRef.current;
      setVal(Math.max(0, baseRef.current + jitter + trend));
    }, intervalMs);
    return () => clearInterval(id);
  }, [base, pct, intervalMs, drift]);

  return val;
}
